import { BaseElement } from 'base';

class BlockTop extends BaseElement {
  action = null;
  #state = null;
  #arrows = new Map();

  onConnect() {
    this.listen(this, 'click', this.#onClick);
    this.listen(this, 'focusout', this.#onFocusOut);
    this.#bindArrows();
  }

  run(action) {
    this.action = action;
    return this;
  }

  setArrowState({
    prevDisabled = false,
    nextDisabled = false,
    hidden = false
  } = {}) {
    this.#state = { prevDisabled, nextDisabled, hidden };
    this.#applyState();
    return this;
  }

  async #bindArrows() {
    if (!this.q('[data-top-action]')) return;
    await customElements.whenDefined('action-button');
    if (!this.isConnected) return;

    this.#arrows = new Map(
      this.qa(
        '[data-top-action="prev"] action-button, [data-top-action="next"] action-button'
      ).map(button => [
        button.closest('[data-top-action]').dataset.topAction,
        button
      ])
    );
    this.#applyState();
  }

  #applyState() {
    const state = this.#state;
    if (!state) return;

    const arrows = [...this.#arrows].map(([reason, button]) => ({
      button,
      off: reason === 'prev' ? state.prevDisabled : state.nextDisabled
    }));
    const focused = arrows.find(({ button }) =>
      button.control?.matches(':focus')
    );

    arrows.forEach(({ button, off }) => {
      if (!off) button.setDisabled(false);
    });
    if (focused && (focused.off || state.hidden))
      this.#moveFocusFrom(focused, arrows, state.hidden);
    this.toggleAttribute('data-arrows-hidden', state.hidden);
    arrows.forEach(({ button, off }) => {
      if (off) button.setDisabled(true);
    });
  }

  #moveFocusFrom(focused, arrows, hidden) {
    const other = hidden
      ? null
      : arrows.find(arrow => arrow !== focused && !arrow.off);
    if (other?.button.control) {
      other.button.control.focus();
      return;
    }

    const cta = this.q('[data-top-action="cta"] [data-action-control]');
    if (cta && !cta.disabled && cta.getAttribute('aria-disabled') !== 'true') {
      cta.focus();
      return;
    }

    const heading = this.q('.block-top__heading');
    if (!heading) return;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }

  #onFocusOut(event) {
    if (event.target.matches('.block-top__heading'))
      event.target.removeAttribute('tabindex');
  }

  #onClick(event) {
    const control = event.target.closest('[data-action-control]');
    const trigger = control?.closest('[data-top-action]');
    if (!trigger || !this.contains(trigger)) return;
    if (control.disabled || control.getAttribute('aria-disabled') === 'true')
      return;
    if (!this.action) return;

    const reason = trigger.dataset.topAction;
    if (reason === 'cta') event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true') return;

    this.#dispatch(reason);
  }

  async #dispatch(reason) {
    try {
      const pending = this.action({ reason, id: this.id });
      if (typeof pending?.then !== 'function') return;
      this.setAttribute('aria-busy', 'true');
      await pending;
    } catch (error) {
      console.error('[block-top] action failed:', error);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('block-top'))
  customElements.define('block-top', BlockTop);
